// ABOUTME: The foot of the job details page: the CV line and the apply button for a talent who may apply, a line for
// ABOUTME: one who may not, the way to an application they already have, the confirm sheet, and where they land after.
import { useCallback, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { AppButton } from 'components/AppButton';
import { BottomSheet } from 'components/BottomSheet';
import { Text } from 'components/Text';
import { PATHS } from 'config/paths';
import { sendAtsJobApplied, sendAtsJobApplyFailed } from 'modules/analytics';
import { UNNAMED_ERROR_CODE } from 'modules/applications';
import { getTrans, TranslationKey } from 'modules/i18n';
import { ApiError } from 'utils/api';
import {
  APPLY_NOT_POSSIBLE_ERROR_CODE,
  JOB_LISTING_NOT_FOUND_ERROR_CODE
} from '../constants';
import {
  useApplyToJobListing,
  useApplyV2Enabled,
  useCanApply,
  useHasProCv
} from '../hooks';
import { JobListing } from '../types';
import { ApplyFlow } from './apply/ApplyFlow';
import { JobPageFooter } from './JobPageParts';

/** The copy for a refusal, by pro-core's code; anything else reads as the unnamed failure. */
const ERROR_COPY: Record<string, TranslationKey> = {
  [APPLY_NOT_POSSIBLE_ERROR_CODE]: 'jobs.apply.error.notPossible',
  [JOB_LISTING_NOT_FOUND_ERROR_CODE]: 'jobs.apply.error.gone'
};

/** pro-core's code off a refusal, whatever module instance the error class came from; empty for a failure without one. */
const codeOf = (error: unknown): string => {
  if (error instanceof ApiError) return error.code;
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : '';
};

const errorCopy = (code: string): TranslationKey =>
  ERROR_COPY[code] ?? 'jobs.apply.error.unnamed';

export const ApplyFooter = ({ listing }: { listing: JobListing }) => {
  const navigate = useNavigate();
  const { canApply, isLoading } = useCanApply();
  // Until pro-core has answered, the footer says nothing about the CV: a talent with one must never read that it is missing.
  const { hasCv, isLoading: cvUnknown } = useHasProCv();
  const { apply, isApplying, errorCode, reset } = useApplyToJobListing(
    listing.refId
  );
  const [confirming, setConfirming] = useState(false);
  // PJL: with PRO_APPLY_V2 the button opens the three-step flow instead of the confirm sheet.
  const { isEnabled: applyV2 } = useApplyV2Enabled();
  const [flowOpen, setFlowOpen] = useState(false);
  // pro-core's PRO_APPLY_V2 is off for this talent after all (its week check answers 404): today's confirm sheet.
  const openTodaysApply = useCallback(() => {
    setFlowOpen(false);
    setConfirming(true);
  }, []);

  if (isLoading) return null;

  const existing = listing.myApplication;
  // A version raised or closed since: readable, not open to apply (only sent by pro-core with PJL).
  const versionClosed = listing.canApply === false;

  const confirm = async () => {
    try {
      const answer = await apply();
      sendAtsJobApplied({
        jobListingRefId: listing.refId,
        applicationRefId: answer.applicationRefId,
        created: answer.created
      });
      setConfirming(false);
      if (answer.created) {
        // Back in the feed, with the new row in the block and the banner saying the application was sent.
        navigate(PATHS.applications, {
          state: { applicationSent: answer.applicationRefId }
        });
      } else {
        // Nothing to celebrate and something to show them: the application they already had at this opening.
        navigate(`${PATHS.applications}/${answer.applicationRefId}`);
      }
    } catch (error) {
      sendAtsJobApplyFailed({
        jobListingRefId: listing.refId,
        errorCode: codeOf(error) || UNNAMED_ERROR_CODE
      });
    }
  };

  const close = () => {
    setConfirming(false);
    reset();
  };

  return (
    <>
      <JobPageFooter testId="job-listing-apply-footer">
        {!canApply && (
          <Text
            variant="body2"
            color="text.secondary"
            component="p"
            sx={{ textAlign: 'center' }}
          >
            {getTrans('jobs.apply.unavailable')}
          </Text>
        )}
        {canApply && existing && (
          <Button
            component={RouterLink}
            to={`${PATHS.applications}/${existing.refId}`}
            variant="contained"
            size="large"
            fullWidth
            data-testid="job-listing-existing-application"
            sx={{ height: 40, borderRadius: 1.5, fontSize: '14px' }}
          >
            {getTrans('jobs.apply.existing')}
          </Button>
        )}
        {canApply && !existing && versionClosed && (
          <Text
            variant="body2"
            color="text.secondary"
            component="p"
            data-testid="job-listing-version-closed"
            sx={{ textAlign: 'center' }}
          >
            {getTrans('jobs.apply.closedVersion')}
          </Text>
        )}
        {canApply && !existing && !versionClosed && (
          <>
            {!cvUnknown && (
              <Text
                variant="caption"
                color={hasCv ? 'text.secondary' : 'warning.dark'}
                component="p"
                sx={{ textAlign: 'center' }}
              >
                {getTrans(
                  hasCv ? 'jobs.apply.cvOnFile' : 'jobs.apply.cvMissing'
                )}
              </Text>
            )}
            <AppButton
              fullWidth
              testId="job-listing-apply"
              // The three steps depend on whether a CV exists, so the flow waits for that answer.
              disabled={applyV2 && cvUnknown}
              onClick={() =>
                applyV2 ? setFlowOpen(true) : setConfirming(true)
              }
            >
              {getTrans('jobs.apply.action')}
            </AppButton>
            {!cvUnknown && (
              <Link
                component={RouterLink}
                to={PATHS.cvBuilder}
                variant="body2"
                sx={{ textAlign: 'center', fontWeight: 600 }}
              >
                {getTrans(hasCv ? 'jobs.apply.updateCv' : 'jobs.apply.buildCv')}
              </Link>
            )}
          </>
        )}
      </JobPageFooter>

      {applyV2 && flowOpen && (
        <ApplyFlow
          listing={listing}
          hasCv={hasCv}
          open={flowOpen}
          onClose={() => setFlowOpen(false)}
          onUnavailable={openTodaysApply}
        />
      )}

      <BottomSheet
        open={confirming}
        onClose={close}
        ariaLabel={getTrans('jobs.apply.confirm.title')}
      >
        <Box sx={{ p: 3, display: 'grid', gap: 2 }}>
          <Text
            variant="h5"
            component="h2"
            color="text.primary"
            sx={{ fontWeight: 700 }}
          >
            {getTrans('jobs.apply.confirm.title')}
          </Text>
          <Box>
            <Text
              variant="subtitle1"
              color="text.primary"
              sx={{ fontWeight: 700 }}
            >
              {listing.title}
            </Text>
            <Text variant="body2">
              {listing.companyName ?? listing.occupationName}
            </Text>
          </Box>
          <Text variant="body2" color="text.primary">
            {getTrans('jobs.apply.confirm.body')}
          </Text>
          {!cvUnknown && (
            <Text
              variant="caption"
              color={hasCv ? 'text.secondary' : 'warning.dark'}
              component="p"
            >
              {getTrans(hasCv ? 'jobs.apply.cvOnFile' : 'jobs.apply.cvMissing')}
            </Text>
          )}
          {errorCode && (
            <Alert severity="error" data-testid="job-listing-apply-error">
              {getTrans(errorCopy(errorCode))}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <AppButton
              variant="text"
              onClick={close}
              testId="job-listing-apply-cancel"
            >
              {getTrans('jobs.apply.confirm.cancel')}
            </AppButton>
            <AppButton
              onClick={() => void confirm()}
              disabled={isApplying}
              testId="job-listing-apply-confirm"
            >
              {getTrans('jobs.apply.confirm.action')}
            </AppButton>
          </Box>
        </Box>
      </BottomSheet>
    </>
  );
};
